import { Alert, AlertTitle, Box, Grid, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/system";
import { settings } from "app";
import permissions from "app/permissions";
import { DateField, Labeled, TextField, useRecordContext } from "react-admin";
import SecuredComponent from "server/security/SecuredComponent";
import StatusChip from "server/components/StatusChip";
import {
  NacidFailureClassChoices,
  NacidFailureClassColorMap,
  NacidFlowSourceChoices,
  NacidFlowStatusChoices,
  NacidFlowStatusColorMap,
  NacidFlowTypeChoices,
} from "utils/choices";
import { MOBILE_BREAKPOINT, NacidFlowStatus } from "utils/constants";
import CancelFlowDialog from "./CancelFlowDialog";
import RetryFlowDialog from "./RetryFlowDialog";
import { SectionTitle } from "features/SectionTitle";

const label = (choices, value) => choices.find(x => x.id === value)?.name ?? value ?? "—";

const FlowSummary = () => {
  const record = useRecordContext();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  if (!record) return null;

  const isTerminal = record.status === NacidFlowStatus.Completed || record.status === NacidFlowStatus.Cancelled;

  return (
    <>
      <SectionTitle
        title="Основна информация"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />

      <Grid
        container
        spacing={2}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="Статус">
            <StatusChip
              status={record.status}
              choices={NacidFlowStatusChoices}
              colorMap={NacidFlowStatusColorMap}
              size="medium"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="Тип">
            <Typography>{label(NacidFlowTypeChoices, record.type)}</Typography>
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="Задействан от">
            <Typography>{label(NacidFlowSourceChoices, record.source)}</Typography>
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="Стъпка">
            <Typography>
              {record.currentStepOrder ?? 0} / {record.steps?.length ?? 0}
            </Typography>
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="Студент №">
            <TextField source="studentId" />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="Обучение №">
            <TextField
              source="studentEducationId"
              emptyText="—"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="ЕАН">
            <TextField
              source="uan"
              emptyText="—"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="Създаден">
            <DateField
              source="createdOn"
              locales="bg"
              showTime
            />
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="Завършен">
            <DateField
              source="completedOn"
              locales="bg"
              showTime
              emptyText="—"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="Следващ опит">
            <DateField
              source="nextAttemptOn"
              locales="bg"
              showTime
              emptyText="—"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >
          <Labeled label="Идентификатор за проследяване">
            <Typography sx={{ fontFamily: "monospace", fontSize: 13 }}>{record.correlationId}</Typography>
          </Labeled>
        </Grid>
      </Grid>

      {/* A flow waiting on another one is not stuck — it resumes by itself the moment that one completes. */}
      {record.blockedByFlowId ? (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
        >
          Потокът изчаква поток №{record.blockedByFlowId} и ще продължи автоматично, щом онзи приключи.
        </Alert>
      ) : null}

      {record.manualReasonCode ? (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
        >
          <AlertTitle>Изисква ръчна намеса</AlertTitle>
          {record.manualInstructionBg}
          <Typography
            variant="caption"
            display="block"
            sx={{ fontFamily: "monospace", mt: 1 }}
          >
            {record.manualReasonCode}
          </Typography>
        </Alert>
      ) : null}

      {record.failureCode || record.failureMessage ? (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
        >
          <AlertTitle>
            Грешка
            {record.failureClass ? ` — ${label(NacidFailureClassChoices, record.failureClass)}` : ""}
          </AlertTitle>
          {record.failureMessage}
          {record.failureCode ? (
            <Typography
              variant="caption"
              display="block"
              sx={{ fontFamily: "monospace", mt: 1 }}
            >
              {record.failureCode}
            </Typography>
          ) : null}
        </Alert>
      ) : null}

      {record.note ? (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
        >
          <AlertTitle>Бележка</AlertTitle>
          {record.note}
        </Alert>
      ) : null}

      {record.failureClass ? (
        <Box sx={{ mt: 2 }}>
          <StatusChip
            status={record.failureClass}
            choices={NacidFailureClassChoices}
            colorMap={NacidFailureClassColorMap}
          />
        </Box>
      ) : null}

      <SecuredComponent permission={permissions.canManageNacidFlows}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "flex-end", mt: 2 }}>
          <RetryFlowDialog />
          {isTerminal ? null : <CancelFlowDialog />}
        </Box>
      </SecuredComponent>
    </>
  );
};

export default FlowSummary;
